import { Button } from '@chopchop-se/ui';
import type { ArticleCreate, ArticleDetail, ArticleUpdate, BcLink } from '@warehouse/shared';
import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { BcSyncNotice, isBcLocked } from '../components/BcBadge';
import { CheckboxField, FormSection, SelectField, TextField } from '../components/form';
import { useCategories, useSuppliers, useUnits } from '../lookups';

/**
 * The article form, shared by "Ny artikel" and the detail page's Detaljer tab.
 *
 * Fields BC owns on a synced article are rendered disabled with the reason
 * spelled out. That is cosmetic — `ARTICLE_BC_OWNED_FIELDS` is enforced
 * server-side and a PATCH that tries anyway comes back
 * `E_CONFLICT { reason: 'bc_synced' }` — but a disabled field with no
 * explanation is how a register earns a reputation for being broken.
 */

export interface ArticleFormValues {
  articleNo: string;
  name: string;
  nameZh: string;
  categoryId: string;
  baseUnitId: string;
  supplierId: string;
  orderable: boolean;
  priority: string;
  lotTracked: boolean;
  expiryRequired: boolean;
  catchWeight: boolean;
  minStockBase: string;
  maxStockBase: string;
  reorderPointBase: string;
  leadTimeDays: string;
  shelfLifeDays: string;
  isActive: boolean;
}

const EMPTY: ArticleFormValues = {
  articleNo: '',
  name: '',
  nameZh: '',
  categoryId: '',
  baseUnitId: '',
  supplierId: '',
  orderable: true,
  priority: '0',
  lotTracked: true,
  expiryRequired: false,
  catchWeight: false,
  minStockBase: '',
  maxStockBase: '',
  reorderPointBase: '',
  leadTimeDays: '',
  shelfLifeDays: '',
  isActive: true,
};

export function toFormValues(article: ArticleDetail): ArticleFormValues {
  return {
    articleNo: article.articleNo,
    name: article.name,
    nameZh: article.nameZh ?? '',
    categoryId: article.categoryId ?? '',
    baseUnitId: article.baseUnitId,
    supplierId: article.supplierId ?? '',
    orderable: article.orderable,
    priority: String(article.priority),
    lotTracked: article.lotTracked,
    expiryRequired: article.expiryRequired,
    catchWeight: article.catchWeight,
    minStockBase: article.minStockBase ?? '',
    maxStockBase: article.maxStockBase ?? '',
    reorderPointBase: article.reorderPointBase ?? '',
    leadTimeDays: article.leadTimeDays === null ? '' : String(article.leadTimeDays),
    shelfLifeDays: article.shelfLifeDays === null ? '' : String(article.shelfLifeDays),
    isActive: article.isActive,
  };
}

const text = (value: string): string | null => (value.trim().length === 0 ? null : value.trim());
const num = (value: string): number | null => (value.trim().length === 0 ? null : Number(value));

export function toPayload(values: ArticleFormValues): ArticleCreate & ArticleUpdate {
  return {
    articleNo: values.articleNo.trim(),
    name: values.name.trim(),
    nameZh: text(values.nameZh),
    categoryId: values.categoryId || null,
    baseUnitId: values.baseUnitId,
    supplierId: values.supplierId || null,
    orderable: values.orderable,
    priority: Number(values.priority || '0'),
    lotTracked: values.lotTracked,
    expiryRequired: values.expiryRequired,
    catchWeight: values.catchWeight,
    minStockBase: text(values.minStockBase),
    maxStockBase: text(values.maxStockBase),
    reorderPointBase: text(values.reorderPointBase),
    leadTimeDays: num(values.leadTimeDays),
    shelfLifeDays: num(values.shelfLifeDays),
    isActive: values.isActive,
  } as ArticleCreate & ArticleUpdate;
}

export function ArticleForm({
  defaultValues,
  bc,
  submitting,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  defaultValues?: ArticleFormValues;
  bc?: BcLink;
  submitting: boolean;
  submitLabel: string;
  onSubmit: (values: ArticleFormValues) => void;
  onCancel?: () => void;
}): ReactNode {
  const { t } = useTranslation(['masterData', 'common']);
  const { options: unitOptions } = useUnits();
  const { options: categoryOptions } = useCategories();
  const { options: supplierOptions } = useSuppliers();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ArticleFormValues>({ defaultValues: defaultValues ?? EMPTY });

  const lockHint = (field: string): string | undefined =>
    isBcLocked(bc, field) ? t('masterData:bc.fieldHint') : undefined;

  return (
    <form
      onSubmit={(event) => {
        void handleSubmit(onSubmit)(event);
      }}
      className="flex flex-col gap-7"
      noValidate
    >
      {bc ? <BcSyncNotice bc={bc} /> : null}

      <FormSection title={t('masterData:articles.sections.identity')}>
        <TextField
          label={t('masterData:articles.fields.articleNo')}
          hint={lockHint('articleNo') ?? t('masterData:articles.hints.articleNo')}
          disabled={isBcLocked(bc, 'articleNo')}
          error={errors.articleNo ? t('common:common.required') : undefined}
          {...register('articleNo', { required: true })}
        />
        <TextField
          label={t('masterData:articles.fields.name')}
          disabled={isBcLocked(bc, 'name')}
          hint={lockHint('name')}
          error={errors.name ? t('common:common.required') : undefined}
          {...register('name', { required: true })}
        />
        <TextField label={t('masterData:articles.fields.nameZh')} {...register('nameZh')} />
        <CheckboxField label={t('masterData:articles.fields.isActive')} {...register('isActive')} />
      </FormSection>

      <FormSection title={t('masterData:articles.sections.classification')}>
        <SelectField
          label={t('masterData:articles.fields.category')}
          disabled={isBcLocked(bc, 'categoryId')}
          hint={lockHint('categoryId')}
          options={[{ value: '', label: t('masterData:articles.noCategory') }, ...categoryOptions]}
          {...register('categoryId')}
        />
        <SelectField
          label={t('masterData:articles.fields.baseUnit')}
          disabled={isBcLocked(bc, 'baseUnitId')}
          hint={lockHint('baseUnitId') ?? t('masterData:articles.hints.baseUnit')}
          options={unitOptions}
          {...register('baseUnitId', { required: true })}
        />
        <SelectField
          label={t('masterData:articles.fields.supplier')}
          options={[{ value: '', label: t('masterData:articles.noSupplier') }, ...supplierOptions]}
          {...register('supplierId')}
        />
        <TextField
          label={t('masterData:articles.fields.priority')}
          type="number"
          hint={t('masterData:articles.hints.priority')}
          {...register('priority')}
        />
      </FormSection>

      <FormSection title={t('masterData:articles.sections.handling')}>
        <CheckboxField
          label={t('masterData:articles.fields.orderable')}
          hint={t('masterData:articles.hints.orderable')}
          {...register('orderable')}
        />
        <CheckboxField
          label={t('masterData:articles.fields.lotTracked')}
          hint={t('masterData:articles.hints.lotTracked')}
          {...register('lotTracked')}
        />
        <CheckboxField
          label={t('masterData:articles.fields.expiryRequired')}
          hint={t('masterData:articles.hints.expiryRequired')}
          {...register('expiryRequired')}
        />
        <CheckboxField
          label={t('masterData:articles.fields.catchWeight')}
          hint={t('masterData:articles.hints.catchWeight')}
          {...register('catchWeight')}
        />
      </FormSection>

      <FormSection title={t('masterData:articles.sections.stockLevels')}>
        <TextField label={t('masterData:articles.fields.minStock')} inputMode="decimal" {...register('minStockBase')} />
        <TextField label={t('masterData:articles.fields.maxStock')} inputMode="decimal" {...register('maxStockBase')} />
        <TextField
          label={t('masterData:articles.fields.reorderPoint')}
          inputMode="decimal"
          hint={t('masterData:articles.hints.reorderPoint')}
          {...register('reorderPointBase')}
        />
        <TextField
          label={t('masterData:articles.fields.leadTimeDays')}
          type="number"
          hint={t('masterData:articles.hints.leadTimeDays')}
          {...register('leadTimeDays')}
        />
        <TextField
          label={t('masterData:articles.fields.shelfLifeDays')}
          type="number"
          hint={t('masterData:articles.hints.shelfLifeDays')}
          {...register('shelfLifeDays')}
        />
      </FormSection>

      <div className="flex justify-end gap-2">
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            {t('common:actions.cancel')}
          </Button>
        ) : null}
        <Button type="submit" loading={submitting}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
